import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Loader2, KeyRound, ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import InkPhDLogo from '@/components/InkPhDLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ThesisResetPassword — Sprint 7 hotfix.
 *
 * Expects a `?token=...` query param (produced by /auth/forgot-password).
 * User enters a new password + confirmation → POST /api/auth/reset-password
 * → on success, redirect to /thesis/login with a toast.
 */
export default function ThesisResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Reset link is missing or invalid. Request a new one from the "Forgot?" link on the sign-in page.');
  }, [token]);

  const strong = password.length >= 8;
  const match = password && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { setError('Password must be at least 8 characters'); return; }
    if (!match)  { setError('Passwords do not match'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success('Password reset — you can now sign in with your new password.');
      navigate('/thesis/login', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Reset failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950">
      <header className="border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="reset-logo-home">
            <InkPhDLogo size="sm" />
          </Link>
          <Link to="/thesis/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md" data-testid="reset-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-serif text-2xl" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
              <KeyRound className="h-5 w-5 text-[hsl(212,90%,55%)]" />
              Set a new password
            </CardTitle>
            <CardDescription>
              Choose a strong password — at least 8 characters. You'll be signed in after reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Missing reset token.</strong> The link you followed is missing its token.
                  Please request a new one from the sign-in page.
                  <div className="mt-3">
                    <Button asChild size="sm" variant="outline"><Link to="/thesis/forgot-password">Request reset link</Link></Button>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {error && (
                  <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2"
                    data-testid="reset-error">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <div>
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    New password
                  </Label>
                  <Input
                    id="password" type="password" required autoFocus minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                    data-testid="reset-password-input"
                  />
                  <div className="mt-1 flex items-center gap-1 text-[10.5px]">
                    <span className={strong ? 'text-emerald-600' : 'text-slate-400'}>
                      {strong ? '✓' : '○'} At least 8 characters
                    </span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Confirm password
                  </Label>
                  <Input
                    id="confirm" type="password" required minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1"
                    data-testid="reset-confirm-input"
                  />
                  {confirm && (
                    <div className="mt-1 text-[10.5px]">
                      <span className={match ? 'text-emerald-600' : 'text-red-600'}>
                        {match ? '✓ Passwords match' : '✕ Passwords do not match'}
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  type="submit" disabled={submitting || !strong || !match}
                  className="w-full bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white disabled:opacity-50"
                  data-testid="reset-submit-btn">
                  {submitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting…</>
                    : <><CheckCircle2 className="w-4 h-4 mr-2" /> Reset password</>}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
